import { RiCheckboxCircleFill } from "@remixicon/react";
import React, { useEffect, useState } from "react";
import { OnboardingProfile, useGetOnboardingProfileQuery, useSaveOnboardingProfileMutation } from "../../../../__generated__/api";
import Badge from "../../../../components/theme/badge/Badge";
import Button from "../../../../components/theme/button/Button";
import RouterLink from "../../../../components/theme/button/RouterLink";
import Dropdown from "../../../../components/theme/dropdown/Dropdown";
import Icon from "../../../../components/theme/icon/Icon";
import ProgressBar from "../../../../components/theme/progress-bar/ProgressBar";
import Text from "../../../../components/theme/text/Text";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import useRouting, { Paths, useParams } from "../../../../contexts/Routing";
import { STEPS, useProfile } from "./HomeV3";
import Styles from "./HomeV3.module.scss";

const STEPS_LIST = STEPS.flatMap(row => row.actions);

export default function GettingStarted() {
    const { createHistoryPush } = useRouting();

    const { data: onboardingData } = useGetOnboardingProfileQuery();
    const [profile, setProfile] = useState<OnboardingProfile | undefined>(onboardingData?.onboardingProfile ?? undefined);
    const { progressPercent, homeStepsCompleted, isDone } = useProfile(profile);

    const [saveOnboardingProfile] = useSaveOnboardingProfileMutation();

    const { setting } = useParams(Paths.ADMINISTRATOR_SETTING);
    const { setting: idSettings } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const { setting: idChildSettings } = useParams(Paths.ADMINISTRATOR_SETTING_ID_CHILD);

    const selectedSetting = idSettings ?? idChildSettings ?? setting;
    const currentStepIndex = STEPS_LIST.findIndex(step => step.setting === selectedSetting);

    useEffect(() => {
        if (onboardingData?.onboardingProfile) {
            setProfile(onboardingData?.onboardingProfile);
        }
    }, [onboardingData?.onboardingProfile]);

    useEffect(() => {
        const current = STEPS_LIST[currentStepIndex]?.setting;
        if (current != null) {
            LoggingHandler.instance.wrap(async () => {
                const copied = [...homeStepsCompleted, current];
                if (!profile || copied.equals(Array.from(profile.homeStepsCompleted ?? []))) {
                    return;
                }
                const data = await saveOnboardingProfile({
                    variables: {
                        profile: {
                            homeStepsCompleted: copied,
                        },
                    },
                });
                setProfile(data.data?.saveOnboardingProfile ?? profile);
            });
        }
    }, [currentStepIndex, profile]);

    const completeAll = () => {
        LoggingHandler.instance.wrap(async () => {
            const copied = [...homeStepsCompleted, ...STEPS_LIST.map(row => row.setting)].dedupe();
            if (!profile || copied.equals(Array.from(profile.homeStepsCompleted ?? []))) {
                return;
            }
            const data = await saveOnboardingProfile({
                variables: {
                    profile: {
                        homeStepsCompleted: copied,
                    },
                },
            });
            setProfile(data.data?.saveOnboardingProfile ?? profile);
        });
    };

    if (isDone) {
        return null;
    }

    return (
        <div className={Styles["getting-started"]}>
            {currentStepIndex ? (
                <RouterLink
                    size="small"
                    variant="ghost-gray"
                    prefixIcon={<Icon.Back />}
                    metricPath={["onboarding", "get-started-bar", "back"]}
                    to={Paths.ADMINISTRATOR_SETTING}
                    params={{
                        setting: STEPS_LIST[currentStepIndex - 1]?.setting,
                    }}
                >
                    Back
                </RouterLink>
            ) : null}
            <Dropdown>
                <Dropdown.Toggle variant="filter" size="small">
                    <ProgressBar value={progressPercent} variant="donut" shouldShowLabel />
                    <span className={Styles["getting-started-title"]}>
                        Getting Started Guide
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <div className={Styles["getting-started-items"]}>
                        {STEPS_LIST.map(step => (
                            <Dropdown.Item
                                className={Styles["getting-started-item"]}
                                onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING, { setting: step.setting })}
                                prefixIcon={step.icon}
                                iconSize="small"
                                iconVariant="secondary"
                                key={step.setting}
                                metricPath={["onboarding", "get-started-bar", step.setting]}
                                postFixItem={homeStepsCompleted.has(step.setting) ? (
                                    <Icon icon={<RiCheckboxCircleFill />} variant="success" />
                                ) : (
                                    <Icon.Checkmark variant="secondary" />
                                )}
                            >
                                <Text textVariant="body-1-medium">
                                    {step.title}
                                </Text>

                                {step.isOptional && (
                                    <Text
                                        textVariant="body-2-regular"
                                        colorVariant="body-secondary"
                                    >
                                        {" "}
                                        (optional)
                                    </Text>
                                )}

                                {step.isNew && (
                                    <Badge variant="counter" size="small" className={Styles["new-badge"]} isSelected>
                                        New
                                    </Badge>
                                )}

                            </Dropdown.Item>
                        ))}
                        <Button
                            variant="ghost-blue"
                            size="small"
                            metricPath={["onboarding", "get-started-bar", "all"]}
                            onClick={completeAll}
                        >
                            Skip Getting Started Guide
                        </Button>
                    </div>

                </Dropdown.Menu>
            </Dropdown>
            {(currentStepIndex != null && currentStepIndex < STEPS_LIST.length - 1) ? (
                <RouterLink
                    size="small"
                    variant="ghost-blue"
                    to={Paths.ADMINISTRATOR_SETTING}
                    metricPath={["onboarding", "get-started-bar", "next"]}
                    params={{
                        setting: STEPS_LIST[currentStepIndex + 1]?.setting,
                    }}
                    postfixIcon={<Icon.Next />}
                >
                    Next
                </RouterLink>
            ) : null}
        </div>
    );
}
